import { type IWebAnalyticsProviderStatus } from '@mahawi/eshop-common';
import { Badge, Tag } from 'antd';
import { type ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import React from 'react';

import { EDateFormat } from '../../fragments/date-format';
import Table from '../table';

interface IProviderStatusRow extends IWebAnalyticsProviderStatus {
  key: string;
}

const RUN_STATUS_COLORS: Record<string, string> = {
  SUCCEEDED: 'green',
  FAILED: 'red',
  SKIPPED: 'default',
};

export const formatLastSuccess = (lastSuccessAt: string | null): string =>
  lastSuccessAt
    ? dayjs(lastSuccessAt).format(EDateFormat.DATE_TIME_SHORT)
    : 'Never';

export const runStatusColor = (lastRunStatus: string | null): string =>
  (lastRunStatus && RUN_STATUS_COLORS[lastRunStatus]) || 'default';

export const failureBadgeStatus = (
  consecutiveFailures: number,
): 'success' | 'error' => (consecutiveFailures > 0 ? 'error' : 'success');

export const toProviderStatusRows = (
  providers: IWebAnalyticsProviderStatus[],
): IProviderStatusRow[] =>
  providers.map(
    (provider: IWebAnalyticsProviderStatus): IProviderStatusRow => ({
      ...provider,
      key: provider.provider,
    }),
  );

export const providerStatusColumns: ColumnType<IProviderStatusRow>[] = [
  {
    title: 'Provider',
    dataIndex: 'provider',
  },
  {
    title: 'Last success',
    dataIndex: 'lastSuccessAt',
    render: (lastSuccessAt: string | null): string =>
      formatLastSuccess(lastSuccessAt),
  },
  {
    title: 'Last run status',
    dataIndex: 'lastRunStatus',
    render: (lastRunStatus: string | null): React.ReactElement => (
      <Tag color={runStatusColor(lastRunStatus)}>
        {lastRunStatus ?? 'Unknown'}
      </Tag>
    ),
  },
  {
    title: 'Consecutive failures',
    dataIndex: 'consecutiveFailures',
    render: (consecutiveFailures: number): React.ReactElement => (
      <Badge
        status={failureBadgeStatus(consecutiveFailures)}
        text={String(consecutiveFailures)}
      />
    ),
  },
];

function ProviderStatus({
  providers,
  isLoading,
}: {
  providers: IWebAnalyticsProviderStatus[];
  isLoading: boolean;
}): React.ReactElement {
  return (
    <Table
      columns={providerStatusColumns as ColumnType<object>[]}
      dataSource={toProviderStatusRows(providers)}
      isLoading={isLoading}
      emptyText="No collection has run yet"
    />
  );
}

export default ProviderStatus;
